import { useState } from "react";
import currencyCodes from "currency-codes";
import ConfirmPrompt from "../../components/ConfirmPrompt";
import { useOnboarding } from "../context/OnboardingContext";
import { generateIncomeSummary } from "./SummaryStage";

export default function BankAccountStage({ onComplete }) {
  const {
    state,
    createBankAccount,
    updateBankAccount,
    deleteBankAccount,
    bulkSaveBankAccounts,
    setCurrency,
    getBankAccounts,
    isSaved,
  } = useOnboarding();

  const bankAccounts = getBankAccounts();
  const selectedCurrency = state.currency || "INR";

  const [currentAccount, setCurrentAccount] = useState({
    name: "",
    balance: "",
    id: null, // null for new, set to id when editing
  });
  const [isAdding, setIsAdding] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmModalConfig, setConfirmModalConfig] = useState(null);

  // Get currency options
  const currencyOptions = currencyCodes
    .codes()
    .map((code) => {
      const currency = currencyCodes.code(code);
      return {
        code: code,
        name: currency.currency,
        symbol: currency.symbol || code,
      };
    })
    .filter((currency) => currency.symbol); // Only show currencies with symbols

  // Get current currency symbol
  const getCurrencySymbol = () => {
    const currency = currencyOptions.find((c) => c.code === selectedCurrency);
    return currency ? currency.symbol : "₹";
  };

  const validateAccount = (account) => {
    const newErrors = {};

    if (!account.name || account.name.trim() === "") {
      newErrors.name = "Bank name is required";
    }

    if (!account.balance || account.balance === "") {
      newErrors.balance = "Balance is required";
    } else if (isNaN(account.balance) || parseFloat(account.balance) < 0) {
      newErrors.balance = "Balance must be a valid positive number";
    }

    if (
      bankAccounts.filter((existing_account) => {
        return existing_account.name == account.name.trim();
      }).length >= 1
    ) {
      newErrors.name = "Account with this name already exists";
    }

    return newErrors;
  };

  const handleAddAccount = async () => {
    const validationErrors = validateAccount(currentAccount);

    if (Object.keys(validationErrors).length === 0) {
      if (currentAccount.id) {
        // Editing existing account
        try {
          await updateBankAccount(currentAccount.id, {
            name: currentAccount.name.trim(),
            balance: currentAccount.balance,
            currency: selectedCurrency,
          });
          setCurrentAccount({ name: "", balance: "", id: null });
          setErrors({});
          setIsAdding(false);
        } catch (error) {
          // Error already handled in Context
        }
      } else {
        // Creating new account
        createBankAccount({
          name: currentAccount.name.trim(),
          balance: currentAccount.balance,
          currency: selectedCurrency,
        });
        setCurrentAccount({ name: "", balance: "", id: null });
        setErrors({});
        setIsAdding(false);
      }
    } else {
      setErrors(validationErrors);
    }
  };

  const handleRemoveAccount = async (account) => {
    const result = await deleteBankAccount(account.id);

    // If the result is a bare boolean/null, the delete was handled without a modal.
    if (!result || !result.show) {
      return;
    }

    const modalConfig = result;

    // Show confirm modal
    setConfirmModalConfig({
      ...modalConfig,
      body: (
        <div>
          <p>
            This Bank account is associated with the below income sources. Deleting this will also
            delete the below income sources.
          </p>
          {generateIncomeSummary(
            null,
            modalConfig.relatedIncomes || [],
            modalConfig.currency || selectedCurrency
          )}
        </div>
      ),
      onConfirm: async () => {
        const success = await modalConfig.onConfirm();
        if (success) {
          setConfirmModalConfig(null);
        }
      },
      onCancel: () => {
        setConfirmModalConfig(null);
      },
    });
  };

  const handleEditAccount = (account) => {
    setCurrentAccount({
      name: account.name,
      balance: account.balance.toString(),
      id: account.id,
    });
    setIsAdding(true);
  };

  const handleSubmit = async () => {
    if (bankAccounts.length === 0) {
      alert("Please add at least one bank account.");
      return;
    }

    setIsSubmitting(true);

    try {
      await bulkSaveBankAccounts();
      onComplete();
    } catch (error) {
      // Error already handled in Context
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="row justify-content-center">
        <div className="col-md-8">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mb-0">Step 1: Add Bank Accounts</h5>
                <small className="text-muted">
                  Add one or more bank accounts to track your finances
                </small>
              </div>
            </div>
            <div className="card-body">
              {/* Currency Selection */}
              <div className="mb-4">
                <h6>Currency Selection</h6>
                <div className="mb-3">
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="bi bi-cash text-success"></i>
                    </span>
                    <select
                      className="form-control"
                      value={selectedCurrency}
                      onChange={(e) => setCurrency(e.target.value)}
                    >
                      {currencyOptions.map((currency) => (
                        <option key={currency.code} value={currency.code}>
                          {currency.code} - {currency.name} ({currency.symbol})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="alert alert-warning" role="alert">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  <strong>Important:</strong> This currency selection will be static across the
                  entire application and cannot be changed in the future. Please choose carefully.
                </div>
              </div>

              {/* Existing Accounts List created_at */}
              {bankAccounts.length > 0 && (
                <div className="mb-4">
                  <h6>Your Bank Accounts ({bankAccounts.length})</h6>
                  {/* Cascade Delete Confirmation (rendered when needed) */}
                  {confirmModalConfig && (
                    <ConfirmPrompt
                      show={confirmModalConfig.show}
                      modalSize={confirmModalConfig.modalSize}
                      title={confirmModalConfig.title}
                      body={confirmModalConfig.body}
                      onConfirm={confirmModalConfig.onConfirm}
                      onCancel={confirmModalConfig.onCancel}
                      id={confirmModalConfig.id}
                    />
                  )}

                  <div className="list-group">
                    {bankAccounts.map((account, index) => (
                      <div
                        key={index}
                        className="list-group-item d-flex justify-content-between align-items-center"
                      >
                        <div>
                          <strong>{account.name}</strong>{" "}
                          {isSaved(account) ? (
                            <span className="badge bg-success">Saved</span>
                          ) : (
                            <span className="badge bg-primary">New</span>
                          )}
                          <br />
                          <small className="text-muted">
                            Balance: {getCurrencySymbol()}
                            {account.balance?.toLocaleString() || account.balance}
                          </small>
                        </div>
                        <div>
                          <button
                            className="btn btn-sm btn-outline-primary me-2"
                            onClick={() => handleEditAccount(account)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleRemoveAccount(account)}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add New Account Form */}
              {isAdding ? (
                <div className="card mb-3">
                  <div className="card-body">
                    <h6>{currentAccount.id ? "Edit Bank Account" : "Add New Bank Account"}</h6>
                    <div className="mb-3">
                      <label className="form-label">Bank Name *</label>
                      <input
                        type="text"
                        className={`form-control ${errors.name ? "is-invalid" : ""}`}
                        placeholder="e.g., Chase Bank, Wells Fargo"
                        value={currentAccount.name}
                        onChange={(e) =>
                          setCurrentAccount({
                            ...currentAccount,
                            name: e.target.value,
                          })
                        }
                      />
                      {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Current Balance *</label>
                      <div className="input-group">
                        <span className="input-group-text">{getCurrencySymbol()}</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className={`form-control ${errors.balance ? "is-invalid" : ""}`}
                          placeholder="0.00"
                          value={currentAccount.balance}
                          onChange={(e) =>
                            setCurrentAccount({
                              ...currentAccount,
                              balance: e.target.value,
                            })
                          }
                        />
                        {errors.balance && <div className="invalid-feedback">{errors.balance}</div>}
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      <button className="btn btn-primary" onClick={handleAddAccount}>
                        {currentAccount.id ? "Update Account" : "Add Account"}
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={() => {
                          setIsAdding(false);
                          setCurrentAccount({ name: "", balance: "", id: null });
                          setErrors({});
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <button className="btn btn-outline-primary mb-3" onClick={() => setIsAdding(true)}>
                  + Add Bank Account
                </button>
              )}

              {/* Action Buttons */}
              <div className="d-flex justify-content-between">
                <button
                  className="btn btn-primary"
                  onClick={handleSubmit}
                  disabled={isSubmitting || bankAccounts.length === 0}
                >
                  {isSubmitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Saving...
                    </>
                  ) : (
                    `Continue with ${bankAccounts.length} account${
                      bankAccounts.length !== 1 ? "s" : ""
                    }`
                  )}
                </button>
              </div>

              {/* Progress Indicator */}
              <div className="mt-3">
                <div className="progress" style={{ height: "4px" }}>
                  <div
                    className="progress-bar"
                    role="progressbar"
                    style={{ width: "33%" }}
                    aria-valuenow="33"
                    aria-valuemin="0"
                    aria-valuemax="100"
                  ></div>
                </div>
                <small className="text-muted">Step 1 of 3</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
